/**
 * “TA 的档案”页（/me/dna）上的“性别与生日”卡（PET-FACT-01 F1）。
 * - 从来没存过（version=0）只写“还没填 TA 的性别和生日”；存过的照列，选的就是不确定或不知道也列（2531 ADVISORY-R14 第 1 条）。
 * - 只读：性别写“男孩 / 女孩 / 不确定”；出生日期直接用服务端的 birth_label（写法由服务端定，前端照原样显示），不知道写“不知道”；
 *   岁数只在服务端给了 age 时出一行（approximate 写“约 N 岁”）；“为 TA 选定的生日”单独一行，没选不出。前端不拼日期、不算岁数。
 *   值来源是 platform_original（领养来的原创居民）时轻标“星球设定”。
 * - 家庭管理员看到“填写”（从没存过）或“修改”，就地展开成表单；共同照顾者只读，下面一行说明。
 * - 保存整体提交，expected_version 用进入编辑那一刻读到的版本（首存为 0）；请求体没变的重试沿用原来的幂等键，改过再发换新键；
 *   服务端说这个键用错了（同键不同内容）也换新键。保存键的两种 409 只说人话，原话和错误码不上页面（X-09）。
 * - 一只宠物一张卡：换了宠物整个重挂，编辑中的表单不带到下一只。
 *   改到“星球设定”时先提醒“会和 TA 过去的设定不一样”，下面的“保存”停用，点提醒里的“仍然保存”才发（能不能改由 c84a 定，这句先做着）。
 *   成功：写回缓存、证件缓存失效、回到只读并说“已保存”；409 照 settleFactsConflict 认（CREATE-PET-RED-01，I 10-09）：带了当初那次的回包、
 *   或重新读到的就是这次要存的，算已保存；还在处理只在常驻播报区里说“还在保存，稍等一下再看”、不自己重发，再点保存（补选性别再点同一个）
 *   原样重发同键同体；读到的变成了别的，两边并排写出来等主人选（“用这次填的”才覆盖，“保留现在的”不发请求、回到只读）；
 *   读不到最新的一份就留在原处说一句；“已保存”“还在保存”都放在常驻的播报区里（一直在卡里，只换字，礼貌播报）；422 按原因码说人话；403 说清并刷新家庭列表；
 *   失败时只说人话；错误码、原因码、request_id 不上页面，也不做折叠区（用户 10-09 定稿 X-09／U-26：玩家端不显示技术代码、请求编号、错误码，也不做“展开看详情”的折叠区，要查只在运营后台查）。
 * - 有没保存的改动时由页面的离开提醒一起管（onState 报给页面）；档案的“改一改”开着时这张卡不给编辑（locked）。
 * - 演示模式（服务抛“能力未接入”）只说演示里没有这一项，不编数据、不放按钮。
 */
import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { PetCoreFactsView, PetSex } from "@/shared/contracts";
import { isApiError } from "@/shared/api/errors";
import { newIdempotencyKey } from "@/shared/api/idempotency";
import { env } from "@/shared/config/env";
import { useServices } from "@/shared/services/registry";
import { Button, ErrorState, LoadingState } from "@/shared/ui";
import { FactsConflictLines } from "./FactsConflict";
import { FactsFields } from "./FactsFields";
import { factsKey, invalidateCredentials, useHomeTimeZone, usePetFacts } from "./factsData";
import {
  ageText,
  changedPlatformGroups,
  chosenText,
  EMPTY_FACTS,
  FACTS_BUSY_TEXT,
  factsProblems,
  factsSaveFailureText,
  formFromView,
  isEmptyFacts,
  isFactsBusy,
  isFactsKeyRejected,
  isFactsVersionConflict,
  neverSaved,
  SEX_TEXT,
  sameFacts,
  settleFactsConflict,
  sourceTag,
  storedFactsMatch,
  updateFrom,
  type FactsForm,
} from "./factsModel";
import "./facts.css";

interface EditSession {
  /** 进入编辑时读到的那一份：保存带它的版本号，“有没有改动”也跟它比 */
  base: PetCoreFactsView;
  form: FactsForm;
  /** 上一次发出去的那一份（内容、键、版本）：内容没变的重试原样重发，改过就是新的一次、换新键（同一个键配不同请求体服务端回 409） */
  sent: { form: FactsForm; key: string; version: number } | null;
  /** 改到“星球设定”的值时已经看过那句提醒、点了“仍然保存”；表单再变就要重新提醒 */
  platformOk: boolean;
  /** 正在显示“会和 TA 过去的设定不一样”的提醒 */
  platformAsk: boolean;
}
export interface FactsCardState {
  editing: boolean;
  dirty: boolean;
}

interface PetFactsCardProps {
  petId: string;
  userId: string | null;
  canManage: boolean;
  locked?: boolean;
  onState?: (state: FactsCardState) => void;
}

/** 编辑状态只属于当前账号、宠物和可编辑权限；身份或权限变化后不带入旧表单或焦点请求。 */
export function PetFactsCard(props: PetFactsCardProps) {
  const { pets } = useServices();
  const [serviceOwner, setServiceOwner] = useState({ pets, epoch: 0 });
  // 服务主体改变也只重挂本卡；既有账号/宠物/权限 key 语义保持。
  if (serviceOwner.pets !== pets) setServiceOwner({ pets, epoch: serviceOwner.epoch + 1 });
  // live 未确认账号时不挂载读取 hook，也不借账号缓存显示私密档案。
  if (env.dataMode === "live" && !props.userId?.trim()) {
    return <section className="ps-dna-card ps-facts-card" aria-label="性别与生日" data-testid="pet-facts"><p className="ps-facts-note">登录以后就能接着看。</p></section>;
  }
  return <FactsServiceFor key={serviceOwner.epoch} {...props} />;
}

function FactsServiceFor(props: PetFactsCardProps) {
  return <FactsCardFor key={JSON.stringify([props.userId, props.petId, props.canManage, Boolean(props.locked)])} {...props} />;
}

function FactsCardFor({ petId, userId, canManage, locked = false, onState }: PetFactsCardProps) {
  const { pets } = useServices();
  const queryClient = useQueryClient();
  const titleId = useId();
  const facts = usePetFacts(petId, userId);
  const homeTimeZone = useHomeTimeZone(petId);
  const view = facts.data;
  const [edit, setEdit] = useState<EditSession | null>(null);
  const [notice, setNotice] = useState<"saved" | null>(null);
  // 保存时和服务端现在记的对不上（真冲突）：读到的那一份、这次填的、覆盖用的版本和键（覆盖没收到回音再点，原样重发）
  const [conflict, setConflict] = useState<{ stored: PetCoreFactsView; form: FactsForm; version: number; key: string } | null>(null);
  // 卡里就地补选性别：上一次点的那一份（内容、键、版本）——没收到回音（回执丢了、断网、那次还在处理）再点同一个，原样重发同键同体
  const lastPick = useRef<{ form: FactsForm; key: string; version: number } | null>(null);

  const cardRef = useRef<HTMLElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const mounted = useRef(false);
  const lastOwnedFocus = useRef<HTMLElement | null>(null);
  const focusRequest = useRef<"first" | "trigger" | null>(null);
  // 每个已提交服务主体各有归属；A→B→A 也不会复活 A 的旧异步操作。
  const subject = useMemo(() => Symbol("facts-owner"), [pets, queryClient]);
  const activeSubject = useRef<symbol | null>(null);
  useLayoutEffect(() => {
    activeSubject.current = subject;
    return () => { if (activeSubject.current === subject) activeSubject.current = null; };
  }, [subject]);
  function current(owner = subject) {
    return mounted.current && activeSubject.current === owner && canManage && !locked && (env.dataMode !== "live" || Boolean(userId?.trim()));
  }
  // 保留原读取的取消、重试、错误/鉴权与缓存生命周期；服务切换同样卸载旧读取者。
  async function readCurrent(owner: symbol) {
    if (!current(owner)) throw new Error("Inactive facts context");
    const latest = await facts.refetch();
    if (!current(owner)) throw new Error("Inactive facts context");
    return latest;
  }

  useLayoutEffect(() => {
    mounted.current = true;
    const remember = (event: FocusEvent) => {
      const target = event.target;
      if (target instanceof HTMLElement && target !== document.body) {
        lastOwnedFocus.current = cardRef.current?.contains(target) ? target : null;
      }
    };
    document.addEventListener("focusin", remember);
    return () => {
      mounted.current = false;
      focusRequest.current = null;
      lastOwnedFocus.current = null;
      document.removeEventListener("focusin", remember);
    };
  }, []);

  // 只接续本卡仍持有（或因控件卸载/禁用而失落）的焦点；外部有效焦点优先。
  function ownsFocus() {
    const current = document.activeElement;
    const last = lastOwnedFocus.current;
    return Boolean(cardRef.current?.contains(current)) ||
      (current === document.body && Boolean(last && (!last.isConnected || last.matches(":disabled"))));
  }
  function requestFocus(kind: "first" | "trigger") {
    if (mounted.current && canManage && !locked && ownsFocus()) focusRequest.current = kind;
  }
  useLayoutEffect(() => {
    const request = focusRequest.current;
    focusRequest.current = null;
    if (!request || !mounted.current || !canManage || locked || !ownsFocus()) return;
    const candidates = request === "first"
      ? formRef.current?.querySelectorAll<HTMLElement>("button, select, input")
      : cardRef.current?.querySelectorAll<HTMLElement>("[data-facts-edit-trigger]");
    const target = Array.from(candidates ?? []).find((node) => {
      if (!node.isConnected || node.matches(":disabled") || node.getAttribute("aria-disabled") === "true") return false;
      for (let ancestor: HTMLElement | null = node; ancestor; ancestor = ancestor.parentElement) {
        const style = getComputedStyle(ancestor);
        if (ancestor.hidden || ancestor.hasAttribute("inert") || ancestor.getAttribute("aria-hidden") === "true" || style.display === "none" || style.visibility === "hidden" || style.visibility === "collapse") return false;
      }
      return true;
    });
    target?.focus();
  }, [edit, conflict, notice, canManage, locked, facts.isFetching]);

  const save = useMutation({
    mutationFn: async ({ form, key, version, owner }: { form: FactsForm; key: string; version: number; owner: symbol }) => {
      if (!current(owner)) throw new Error("Inactive facts context");
      try {
        const saved = await pets.saveFacts(petId, updateFrom(form, version), key);
        if (!current(owner)) throw new Error("Inactive facts context");
        return saved;
      } catch (error) {
        // 失效上下文的旧鉴权错误不能传给全局 MutationCache 去刷新新会话。
        if (!current(owner)) throw new Error("Inactive facts context");
        throw error;
      }
    },
    onSuccess: (saved, sent) => savedAs(saved, sent.owner),
    onError: async (error, sent) => {
      if (!current(sent.owner)) return;
      // 409 照 settleFactsConflict 认（CREATE-PET-RED-01，I 10-09 裁定第 3 条）；要重新读的时候用卡片自己的读取（读到的就是卡片显示的最新一份）
      const settled = await settleFactsConflict(error, updateFrom(sent.form, sent.version), async () => {
        const latest = await readCurrent(sent.owner);
        if (!current(sent.owner)) throw new Error("Inactive facts context");
        if (latest.isError || !latest.data) throw latest.error ?? error;
        return latest.data;
      }).catch(() => "unread" as const);
      // 读不到最新的一份：留在原处，下面照旧说“没拿到最新的一份，稍后再试”
      if (!current(sent.owner) || settled === "unread") return;
      if (settled?.kind === "saved") {
        savedAs(settled.view, sent.owner);
        save.reset();
        return;
      }
      if (settled?.kind === "conflict") {
        setEdit(null);
        setConflict({ stored: settled.stored, form: sent.form, version: settled.version, key: newIdempotencyKey("pet-facts") });
        save.reset();
        return;
      }
      // 不是管理员了（被改成共同照顾者）：刷新家庭列表，按钮跟着收起
      if (isApiError(error) && error.status === 403) void queryClient.invalidateQueries({ queryKey: ["households"] });
    },
  });

  function mutateCurrent(sent: { form: FactsForm; key: string; version: number }, owner = subject) {
    if (current(owner)) save.mutate({ ...sent, owner });
  }

  // 存好了（含 409 里认出已经存上）：写回缓存、证件缓存失效、回到只读并说“已保存”
  function savedAs(saved: PetCoreFactsView, owner = subject) {
    if (!current(owner)) return;
    requestFocus("trigger");
    queryClient.setQueryData(factsKey(userId, petId), saved);
    invalidateCredentials(queryClient);
    setEdit(null);
    setConflict(null);
    setNotice("saved");
    lastPick.current = null;
  }

  const dirty = edit ? !sameFacts(edit.form, formFromView(edit.base)) : false;
  // 冲突等主人选的时候，这次填的还没存上：算在编辑、有改动（离开提醒照管，档案的“改一改”照旧让开）
  useEffect(() => {
    onState?.({ editing: Boolean(edit) || Boolean(conflict), dirty: dirty || Boolean(conflict) });
  }, [edit, conflict, dirty, onState]);
  // 卡片卸下时（切宠物、离开这一页）告诉页面“没在编辑、没有改动”，不留下过期的状态
  useEffect(() => () => onState?.({ editing: false, dirty: false }), [onState]);

  function start(view: PetCoreFactsView) {
    if (!current()) return;
    requestFocus("first");
    save.reset();
    setNotice(null);
    setEdit({ base: view, form: formFromView(view), sent: null, platformOk: false, platformAsk: false });
  }
  // 性别还不知道（历史数据）：卡里就地给“男孩 / 女孩”两个按钮，点了直接整体保存一次（带读到的版本、新幂等键），不弹窗、不进编辑态。
  // CREATE-PET-RED-01（I 10-09）：没收到回音（回执丢了、断网、那次还在处理）再点同一个，原样重发同键同体，回包照旧交给 settleFactsConflict 认；
  // 服务端说这个键用错了、或者点了另一个，才是新的一次：换新键，前一次没收到回音时先读一次新版本（versionAfterFailure）
  async function pickSex(view: PetCoreFactsView, sex: Exclude<PetSex, "unknown">) {
    const owner = subject;
    if (!current(owner) || save.isPending) return;
    setNotice(null);
    const last = lastPick.current;
    if (last && last.form.sex === sex && save.isError && !(isFactsKeyRejected(save.error) && !isFactsBusy(save.error))) {
      mutateCurrent(last, owner);
      return;
    }
    const version = last && save.isError ? await versionAfterFailure(view.version, last, owner) : view.version;
    if (!current(owner)) return;
    const sent = { form: { ...formFromView(view), sex }, key: newIdempotencyKey("pet-facts"), version };
    lastPick.current = sent;
    mutateCurrent(sent, owner);
  }
  // 前一次没收到回音以后主人改了要存的值：先读一次新版本。存着的正是前一次发的那一份（其实存上了），就接着它的版本存；
  // 不是（家里别人改过，或者前一次真没存上），照旧用原来读到的版本——别人改过的话服务端回 409，两边并排给主人选，不悄悄盖掉
  async function versionAfterFailure(fallback: number, prior: { form: FactsForm; version: number }, owner = subject) {
    if (!current(owner)) return fallback;
    const latest = await readCurrent(owner).catch((error: unknown) => ({ data: undefined, isError: true, error }));
    if (!current(owner)) return fallback;
    return latest.data && !latest.isError && storedFactsMatch(latest.data, updateFrom(prior.form, prior.version)) ? latest.data.version : fallback;
  }
  function cancel() {
    if (!current()) return;
    requestFocus("trigger");
    save.reset();
    setEdit(null);
  }
  // 冲突：“保留现在的”——不发请求，回到只读（显示的已经是刚读到的最新一份）
  function keepStored() {
    if (!current()) return;
    requestFocus("trigger");
    save.reset();
    setConflict(null);
  }
  // 冲突：“用这次填的”——主人点了才覆盖：这次填的内容、服务端说的最新版本、冲突时就定下的新键
  function overwrite(open: { form: FactsForm; version: number; key: string }) {
    if (!current() || save.isPending) return;
    mutateCurrent({ form: open.form, key: open.key, version: open.version });
  }
  async function send(open: EditSession) {
    const owner = subject;
    if (!current(owner)) return;
    // 内容没变的重试（回执丢了、断网、那次还在处理、再点一次保存）原样重发：同一份内容、同一个键、同一个版本（CREATE-PET-RED-01，I 10-09）。
    // 改过内容、或者服务端说这个键用错了（同键不同内容），才是新的一次：换新键；前一次没收到回音时先读一次新版本（versionAfterFailure）
    const sent = open.sent;
    if (sent && sameFacts(sent.form, open.form) && !(isFactsKeyRejected(save.error) && !isFactsBusy(save.error))) {
      setEdit({ ...open, platformAsk: false });
      mutateCurrent({ form: open.form, key: sent.key, version: sent.version }, owner);
      return;
    }
    const key = newIdempotencyKey("pet-facts");
    const version = sent && save.isError ? await versionAfterFailure(open.base.version, sent, owner) : open.base.version;
    if (!current(owner)) return;
    setEdit({ ...open, sent: { form: open.form, key, version }, platformAsk: false });
    mutateCurrent({ form: open.form, key, version }, owner);
  }
  function submit(event: FormEvent) {
    event.preventDefault();
    if (!current() || !edit || save.isPending || !dirty || factsProblems(edit.form).length) return;
    // 改到领养来的原创居民的“星球设定”：先提醒一句，点“仍然保存”才发（主人能不能改由 c84a 定，这句先做着，I 2026-09-30）
    if (changedPlatformGroups(edit.base, edit.form).length && !edit.platformOk) {
      setEdit({ ...edit, platformAsk: true });
      return;
    }
    send(edit);
  }

  const conflictPending = save.isError && isFactsVersionConflict(save.error);
  // 那次还在处理：不出出错框，只在常驻播报区里说“还在保存，稍等一下再看”
  const busy = save.isError && isFactsBusy(save.error);
  const liveText = busy ? FACTS_BUSY_TEXT : notice === "saved" ? "已保存。" : "";
  // 温和补选：只给能改的管理员、只在还不知道性别时；正在改档案（locked）或已经在编辑时不出
  const sexAsk =
    view && !edit && !conflict && canManage && !locked && view.sex === "unknown" ? (
      <div className="ps-facts-sexask" role="group" aria-label="补选 TA 的性别">
        <p>告诉我们 TA 是男孩还是女孩吧。</p>
        <div>
          <Button size="sm" variant="secondary" disabled={save.isPending} onClick={() => pickSex(view, "male")}>
            男孩
          </Button>
          <Button size="sm" variant="secondary" disabled={save.isPending} onClick={() => pickSex(view, "female")}>
            女孩
          </Button>
        </div>
      </div>
    ) : null;
  const action =
    view && !edit && !conflict && canManage && !locked ? (
      <Button size="sm" variant="secondary" data-facts-edit-trigger="true" disabled={facts.isFetching} onClick={() => start(view)}>
        {neverSaved(view) ? "填写" : "修改"}
      </Button>
    ) : null;

  return (
    <section ref={cardRef} className="ps-dna-card ps-facts-card" aria-labelledby={titleId} data-testid="pet-facts">
      <div className="ps-facts-card__head">
        <h3 id={titleId}>性别与生日</h3>
        {action}
      </div>
      {/* 常驻的播报区（CREATE-PET-RED-01，I 10-09）：一直在卡里，平时空着、不占地方；要说话时只换里面的字（已保存、还在保存），读屏礼貌播报 */}
      <p className={liveText ? "ps-facts-notice" : "visually-hidden"} role="status" aria-live="polite" data-testid="pet-facts-live">
        {liveText}
      </p>
      {!view ? (
        facts.isPending ? (
          <LoadingState lines={1} label="正在读取…" />
        ) : (
          <FactsLoadFailure error={facts.error} onRetry={() => void facts.refetch()} />
        )
      ) : conflict ? (
        // 保存时和服务端现在记的对不上：两边只把有差别的项并排写出来，等主人选；不点就不发覆盖请求（CREATE-PET-RED-01）
        <div className="ps-dna-alert is-conflict" role="alert">
          <FactsConflictLines stored={conflict.stored} body={updateFrom(conflict.form, conflict.version)} />
          {save.isError && !busy ? <p className="ps-facts-alert__text">{factsSaveFailureText(save.error)}</p> : null}
          <div className="ps-facts-actions">
            <Button variant="ghost" disabled={save.isPending} onClick={keepStored}>
              保留现在的
            </Button>
            <Button variant="primary" loading={save.isPending} onClick={() => overwrite(conflict)}>
              用这次填的
            </Button>
          </div>
        </div>
      ) : edit ? (
        <form ref={formRef} className="ps-facts-form" onSubmit={submit} noValidate aria-label="填写 TA 的性别与生日">
          <FactsFields form={edit.form} disabled={save.isPending} homeTimeZone={homeTimeZone} onChange={(form) => setEdit((open) => (open ? { ...open, form, platformOk: false, platformAsk: false } : open))} />
          <div className="ps-facts-clear">
            {/* 性别不再能清回“不确定”（用户 10-08：性别必选；后端拒 422）：清空只清生日两项 */}
            <Button variant="ghost" size="sm" disabled={save.isPending || isEmptyFacts({ ...edit.form, sex: EMPTY_FACTS.sex })} onClick={() => setEdit((open) => (open ? { ...open, form: { ...EMPTY_FACTS, sex: open.form.sex }, platformOk: false, platformAsk: false } : open))}>
              清空生日
            </Button>
            <span>清空后，出生日期显示“不知道”；点保存才生效。</span>
          </div>
          {edit.platformAsk ? (
            <div className="ps-dna-alert is-conflict" role="alert">
              <p>这些是星球给 TA 的设定。改了以后，会和 TA 过去的设定不一样。</p>
              <Button size="sm" variant="secondary" onClick={() => send({ ...edit, platformOk: true })}>
                仍然保存
              </Button>
            </div>
          ) : conflictPending ? (
            <p className="ps-dna-alert" role="alert">
              TA 的档案刚刚有了别的修改，没拿到最新的一份，稍后再试。
            </p>
          ) : save.isError && !busy ? (
            <div className="ps-dna-alert" role="alert">
              <p className="ps-facts-alert__text">{factsSaveFailureText(save.error)}</p>
            </div>
          ) : null}
          <div className="ps-facts-actions">
            <Button variant="ghost" disabled={save.isPending} onClick={cancel}>
              取消
            </Button>
            {/* 星球设定的提醒出来时只留提醒里的“仍然保存”和“取消”，不并排两个保存口（I 2026-09-30） */}
            <Button variant="primary" type="submit" icon="check" loading={save.isPending} disabled={!dirty || factsProblems(edit.form).length > 0 || edit.platformAsk}>
              保存
            </Button>
          </div>
        </form>
      ) : neverSaved(view) ? (
        // 从来没存过：只说还没填，不列“不确定 / 不知道”（那是主人选过之后才有的说法）
        <>
          <p className="ps-facts-empty">还没填 TA 的性别和生日。</p>
          {sexAsk}
        </>
      ) : (
        <dl className="ps-dna-fields">
          <FactRow label="性别" value={view.sex === "unknown" ? "还不知道" : SEX_TEXT[view.sex]} unknown={view.sex === "unknown"} tag={sourceTag(view, "sex")} />
          <FactRow label="出生日期" value={view.birth_label ?? "不知道"} unknown={view.birth_label === null} tag={sourceTag(view, "birth")} />
          {ageText(view.age) ? <FactRow label="年龄" value={ageText(view.age)!} /> : null}
          {chosenText(view) ? <FactRow label="为 TA 选定的生日" value={chosenText(view)!} tag={sourceTag(view, "chosen")} /> : null}
        </dl>
      )}
      {view && !edit && !neverSaved(view) ? sexAsk : null}
      {view && !edit && !conflict && view.sex === "unknown" && save.isError && !busy ? (
        <p className="ps-dna-alert" role="alert">
          {isFactsVersionConflict(save.error) ? "TA 的档案刚刚有了别的修改，没拿到最新的一份，稍后再试。" : factsSaveFailureText(save.error)}
        </p>
      ) : null}
      {view && !canManage ? <p className="ps-facts-note">只有家庭管理员能改这一项。</p> : null}
    </section>
  );
}

function FactRow({ label, value, unknown = false, tag = null }: { label: string; value: string; unknown?: boolean; tag?: string | null }) {
  return (
    <div className="ps-dna-field">
      <dt>{label}</dt>
      <dd>
        {unknown ? <span className="ps-dna-empty">{value}</span> : value}
        {tag ? <span className="ps-facts-tag">{tag}</span> : null}
      </dd>
    </div>
  );
}

/** 读不到：演示模式（能力未接入）温和说明，不编数据；其余按统一错误态，可重试。 */
function FactsLoadFailure({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  if (isApiError(error) && error.isCapabilityUnavailable) {
    return <p className="ps-facts-note">{env.dataMode === "fixture" ? "演示模式里没有 TA 的性别和生日。" : "暂时打不开，过一会儿再来看看。"}</p>;
  }
  return <ErrorState error={error} onRetry={onRetry} />;
}
